import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, CheckCheck, MessageSquare, UserPlus, UserCheck, UserX, FileCheck, FileX, Megaphone, X, ArrowLeft, PackageCheck, PackageX, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ICONS = {
  collab_invite: { icon: UserPlus, color: '#3B82F6', bg: '#EFF6FF', label: 'Collaboration' },
  collab_accepted: { icon: UserCheck, color: '#10B981', bg: '#ECFDF5', label: 'Collaboration' },
  collab_declined: { icon: UserX, color: '#EF4444', bg: '#FEF2F2', label: 'Collaboration' },
  collab_ended: { icon: X, color: '#6B7280', bg: '#F3F4F6', label: 'Collaboration' },
  content_submitted: { icon: FileCheck, color: '#8B5CF6', bg: '#F5F3FF', label: 'Content' },
  content_approved: { icon: Check, color: '#10B981', bg: '#ECFDF5', label: 'Content' },
  content_rejected: { icon: FileX, color: '#EF4444', bg: '#FEF2F2', label: 'Content' },
  campaign_application: { icon: Megaphone, color: '#F59E0B', bg: '#FFFBEB', label: 'Campaign' },
  campaign_accepted: { icon: PackageCheck, color: '#10B981', bg: '#ECFDF5', label: 'Campaign' },
  campaign_rejected: { icon: PackageX, color: '#EF4444', bg: '#FEF2F2', label: 'Campaign' },
  campaign_content_approved: { icon: Check, color: '#10B981', bg: '#ECFDF5', label: 'Campaign' },
  campaign_content_rejected: { icon: FileX, color: '#EF4444', bg: '#FEF2F2', label: 'Campaign' },
  campaign_content_submitted: { icon: FileCheck, color: '#8B5CF6', bg: '#F5F3FF', label: 'Campaign' },
  chat_message: { icon: MessageSquare, color: '#3B82F6', bg: '#EFF6FF', label: 'Chat' },
  default: { icon: Bell, color: '#6B7280', bg: '#F3F4F6', label: 'General' }
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function NotificationsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    if (!token || !user) navigate('/login');
  }, [token, user, navigate]);

  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/notifications/inbox?page=${pageNum}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(prev => append ? [...prev, ...data.notifications] : data.notifications);
      setUnreadCount(data.unread_count);
      setHasMore(data.has_more);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await fetch(`${API}/api/notifications/inbox/${notif.id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Mark read error:', err);
      }
    }
    if (notif.url) navigate(notif.url);
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API}/api/notifications/inbox/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all read error:', err);
    }
  };

  const getIcon = (type) => {
    const config = NOTIF_ICONS[type] || NOTIF_ICONS.default;
    const Icon = config.icon;
    return (
      <div
        className="flex items-center justify-center rounded-full shrink-0"
        style={{ width: 44, height: 44, background: config.bg }}
      >
        <Icon size={20} style={{ color: config.color }} />
      </div>
    );
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  // Group by date
  const grouped = {};
  filteredNotifications.forEach(n => {
    const date = new Date(n.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let key;
    if (date.toDateString() === today.toDateString()) key = 'Today';
    else if (date.toDateString() === yesterday.toDateString()) key = 'Yesterday';
    else key = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(n);
  });

  return (
    <div data-testid="notifications-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-xs text-gray-500">{unreadCount} unread</p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              data-testid="mark-all-read-page-btn"
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg font-medium transition-colors"
            >
              <CheckCheck size={16} /> Mark all read
            </button>
          )}
        </div>
        {/* Filter tabs */}
        <div className="max-w-2xl mx-auto px-4 flex gap-1 pb-2">
          {['all', 'unread'].map(f => (
            <button
              key={f}
              data-testid={`filter-${f}-btn`}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'All' : `Unread (${unreadCount})`}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        {loading && notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Loader2 size={32} className="animate-spin mb-3" />
            <p className="text-sm">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Bell size={40} className="mb-3 opacity-30" />
            <p className="text-base font-medium text-gray-500">
              {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
            </p>
            <p className="text-sm mt-1">
              {filter === 'unread' ? 'You have no unread notifications' : 'Notifications will appear here when you get them'}
            </p>
          </div>
        ) : (
          Object.entries(grouped).map(([dateLabel, notifs]) => (
            <div key={dateLabel} className="mb-6">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                {dateLabel}
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                {notifs.map(notif => {
                  const config = NOTIF_ICONS[notif.type] || NOTIF_ICONS.default;
                  return (
                    <button
                      key={notif.id}
                      data-testid={`notification-page-item-${notif.id}`}
                      onClick={() => handleClick(notif)}
                      className={`w-full flex items-start gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors ${
                        !notif.read ? 'bg-rose-50/30' : ''
                      }`}
                    >
                      {getIcon(notif.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: config.color, background: config.bg }}>
                            {config.label}
                          </span>
                          <span className="text-xs text-gray-400">{timeAgo(notif.created_at)}</span>
                        </div>
                        <p className={`text-sm mt-1 leading-snug ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notif.title}
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5">{notif.body}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0 mt-3" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {hasMore && (
          <div className="flex justify-center py-4">
            <button
              data-testid="load-more-btn"
              onClick={() => fetchNotifications(page + 1, true)}
              disabled={loading}
              className="px-6 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load more notifications'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
